import { GameEngine } from '../game/engine';
import {
  bottleneck,
  extendRoute,
  pushRoute,
  reachesSink,
  routeNodes,
  sinkId,
  startRoute,
  type FlowRoute,
  type RouteRejection,
} from '../game/flowRouting';
import { calculateStars, type Level } from '../models/level';
import { problemTypeRule, worldById } from '../models/world';
import { type TutorialCard } from '../game/tutorialData';
import { GraphCanvasRenderer, type CanvasState } from '../render/graphCanvas';
import { themeFor } from '../render/theme';
import { audio } from '../audio/audioService';
import { readSafeArea, watchResize } from './stage';

export interface PuzzleResult {
  readonly level: Level;
  readonly score: number;
  readonly stars: number;
  readonly selectedEdgeIds: readonly string[];
  readonly edgeFlows?: Readonly<Record<string, number>>;
  readonly durationMs: number;
  readonly hintsUsed: number;
  readonly undoCount: number;
  readonly edgesSelectedDuringSession: number;
}

export interface PuzzleCallbacks {
  readonly onExit: () => void;
  readonly onSolved: (result: PuzzleResult) => void;
  /** Fires once, whether the briefing was read to the end or skipped by playing. */
  readonly onBriefingRead?: () => void;
}

/** Header wording; campaign runs use the defaults derived from the level. */
export interface PuzzlePresentation {
  readonly kicker?: string;
  readonly calendar?: string;
  /** Shown on the run order one card at a time before the objective. */
  readonly briefing?: readonly TutorialCard[];
}

export class PuzzleScreen {
  private readonly root: HTMLElement;
  private readonly level: Level;
  private readonly themeId: string;
  private readonly engine: GameEngine;
  private readonly callbacks: PuzzleCallbacks;
  private readonly presentation: PuzzlePresentation;

  private renderer!: GraphCanvasRenderer;
  private canvas!: HTMLCanvasElement;
  private selected = new Set<string>();
  private flows = new Map<string, number>();
  private route: FlowRoute = { head: '', steps: [] };
  private flowHistory: Map<string, number>[] = [];
  private hovered: string | null = null;
  private hoverFrame = 0;
  private hintTier = 0;
  private undoCount = 0;
  private edgesSelectedDuringSession = 0;
  private readonly startedAt = Date.now();
  private message = '';
  private note = '';
  private orderRead = false;
  private briefingStep = 0;
  private stopWatchingResize: () => void = () => {};

  constructor(
    root: HTMLElement,
    level: Level,
    themeId: string,
    callbacks: PuzzleCallbacks,
    presentation: PuzzlePresentation = {},
  ) {
    this.root = root;
    this.level = level;
    this.themeId = themeId;
    this.engine = new GameEngine(level);
    this.callbacks = callbacks;
    this.presentation = presentation;
  }

  private get isFlowLevel(): boolean {
    return this.level.type === 'maxFlow';
  }

  private get isCutLevel(): boolean {
    return this.level.type === 'minCut' || this.level.type === 'interdiction';
  }

  mount(): void {
    const world = worldById(this.level.worldIndex);
    const calendar =
      this.presentation.calendar ??
      calendarMarkup(this.level.levelIndex, world?.totalLevels ?? this.level.levelIndex);
    this.root.innerHTML = `
      <div class="puzzle">
        <div class="stage puzzle__stage" data-role="stage">
          <canvas data-role="canvas" aria-label="Puzzle graph"></canvas>
        </div>

        <header class="hud hud--top puzzle__bar">
          <button class="btn btn--ghost btn--icon plate" data-action="exit" aria-label="Back">←</button>
          <article class="note note--order puzzle__order is-open" data-role="order">
            <span class="note__staple" aria-hidden="true"></span>
            <div data-role="order-sheet">${this.orderMarkup()}</div>
            <button class="note__toggle" data-role="order-toggle" aria-expanded="true"
                    aria-controls="run-order-fold" aria-label="Fold the run order"></button>
          </article>
          <aside class="plate puzzle__side">
            ${calendar}
            <div class="puzzle__score" data-role="score"></div>
          </aside>
        </header>

        <p class="hud hud--bottom puzzle__message" data-role="message" role="status"></p>
        <article class="hud hud--bl note note--field puzzle__hint" data-role="hint" hidden></article>

        <footer class="hud hud--br plate puzzle__actions">
          ${this.isFlowLevel ? '<button class="btn btn--ghost" data-action="undo">Undo</button>' : ''}
          <button class="btn btn--ghost" data-action="reset">Reset</button>
          <button class="btn btn--ghost" data-action="hint">Hint</button>
          <button class="btn btn--primary" data-action="submit">Submit</button>
        </footer>

        <article class="note puzzle__rotate" data-role="rotate" role="note">
          <span class="note__staple" aria-hidden="true"></span>
          <p>Turn your phone upright. The streets need the room.</p>
          <button class="btn btn--primary btn--small" data-action="dismiss-rotate">Play anyway</button>
        </article>
      </div>
    `;

    this.canvas = this.root.querySelector('[data-role="canvas"]')!;
    this.renderer = new GraphCanvasRenderer(this.canvas);

    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
    this.canvas.addEventListener('pointermove', this.handlePointerMove);
    this.canvas.addEventListener('pointerleave', this.handlePointerLeave);
    this.root.addEventListener('click', this.handleClick);
    this.stopWatchingResize = watchResize(() => this.layout());

    if (this.isFlowLevel) {
      for (const edge of this.level.graph.edges) this.flows.set(edge.id, 0);
      this.route = startRoute(this.level.graph);
      window.addEventListener('keydown', this.handleKeyDown);
    }

    this.layout();
    this.updateStatus();
  }

  unmount(): void {
    cancelAnimationFrame(this.hoverFrame);
    this.stopWatchingResize();
    window.removeEventListener('keydown', this.handleKeyDown);
    this.root.removeEventListener('click', this.handleClick);
    this.renderer.dispose();
  }

  private get order(): HTMLElement {
    return this.root.querySelector<HTMLElement>('[data-role="order"]')!;
  }

  private layout(): void {
    const stage = this.root.querySelector<HTMLElement>('[data-role="stage"]');
    if (stage) this.renderer.setInsets(readSafeArea(stage));
    this.renderer.resize();
    this.paint();
  }

  private setOrderOpen(open: boolean): void {
    const order = this.order;
    order.classList.toggle('is-open', open);
    const toggle = order.querySelector<HTMLElement>('[data-role="order-toggle"]');
    toggle?.setAttribute('aria-expanded', String(open));
    toggle?.setAttribute('aria-label', open ? 'Fold the run order' : 'Unfold the run order');
  }

  /** The order stays unfolded until the first move; after that hover or a tap brings it back. */
  private foldOrderAfterFirstMove(): void {
    this.endBriefing();
    if (this.orderRead) return;
    this.orderRead = true;
    this.setOrderOpen(false);
  }

  private get briefing(): readonly TutorialCard[] {
    return this.presentation.briefing ?? [];
  }

  private get briefingCard(): TutorialCard | undefined {
    return this.briefing[this.briefingStep];
  }

  private orderMarkup(): string {
    const card = this.briefingCard;
    if (card) {
      const step = this.briefingStep + 1;
      const total = this.briefing.length;
      return `
        <p class="note__kicker">Briefing ${step} of ${total}</p>
        <h1 class="note__title">${escapeHtml(card.title)}</h1>
        <div class="note__fold" id="run-order-fold">
          <p class="note__body">${escapeHtml(card.body)}</p>
          <p class="note__nav">
            <button class="btn btn--primary btn--small" data-action="briefing-next">
              ${step === total ? 'Start the run' : 'Next'}
            </button>
          </p>
        </div>
      `;
    }

    const world = worldById(this.level.worldIndex);
    const kicker = this.presentation.kicker ?? `Run order · ${world?.name ?? 'Gearfall'}`;
    return `
      <p class="note__kicker">${escapeHtml(kicker)}</p>
      <h1 class="note__title">${escapeHtml(this.level.title)}</h1>
      <div class="note__fold" id="run-order-fold">
        <p class="note__body">${escapeHtml(this.level.objective)}</p>
        <p class="note__rule">${escapeHtml(problemTypeRule(this.level.type))}</p>
      </div>
    `;
  }

  private renderOrder(): void {
    const sheet = this.order.querySelector<HTMLElement>('[data-role="order-sheet"]')!;
    sheet.innerHTML = this.orderMarkup();
  }

  private advanceBriefing(): void {
    audio.play('buttonTap');
    if (this.briefingStep + 1 >= this.briefing.length) {
      this.endBriefing();
      this.setOrderOpen(true);
      return;
    }
    this.briefingStep++;
    this.renderOrder();
  }

  private endBriefing(): void {
    if (!this.briefingCard) return;
    this.briefingStep = this.briefing.length;
    this.renderOrder();
    this.callbacks.onBriefingRead?.();
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.route.steps.length === 0) return;
    this.route = startRoute(this.level.graph);
    this.note = '';
    audio.play('edgeDeselect');
    this.paint();
    this.updateStatus();
  };

  private traceFlow(edgeId: string): void {
    const graph = this.level.graph;
    const result = extendRoute(graph, this.flows, this.route, edgeId);
    if (!result.ok) {
      this.message = this.routeRejection(result.reason);
      audio.play('invalidMove');
      this.paint();
      this.updateStatus();
      return;
    }

    this.route = result.route;
    this.message = '';
    if (result.steppedBack) {
      this.undoCount++;
      audio.play('edgeDeselect');
    } else {
      this.edgesSelectedDuringSession++;
      audio.play('edgeSelect');
    }

    if (reachesSink(graph, this.route)) {
      const amount = bottleneck(graph, this.flows, this.route);
      this.flowHistory.push(new Map(this.flows));
      this.flows = pushRoute(graph, this.flows, this.route);
      this.route = startRoute(graph);
      this.note = `Valves open: ${amount} more through the line.`;
      audio.play('hintReveal');
    } else {
      const names = routeNodes(graph, this.route).map((id) => this.nodeName(id));
      const room = bottleneck(graph, this.flows, this.route);
      this.note = `${names.join(' → ')} · room for ${room} · reach ${this.nodeName(sinkId(graph) ?? '')} to open the valves`;
    }

    this.selected = new Set([...this.flows.entries()].filter(([, f]) => f > 0).map(([k]) => k));
    this.paint();
    this.updateStatus();
  }

  private routeRejection(reason: RouteRejection): string {
    const head = this.nodeName(this.route.head);
    switch (reason) {
      case 'noSource':
        return 'This level has no source to trace from.';
      case 'notAdjacent':
        return `Your route stands at ${head} — pick a pipe leading out of it.`;
      case 'saturated':
        return 'That pipe is already full.';
      case 'wrongWay':
        return 'That pipe runs the other way, and nothing flows back yet.';
      case 'revisit':
        return 'Your route already passed through that valve.';
    }
  }

  private undoFlow(): void {
    const previous = this.flowHistory.pop();
    if (this.route.steps.length > 0) {
      this.route = startRoute(this.level.graph);
    } else if (previous) {
      this.flows = previous;
      this.undoCount++;
    } else {
      return;
    }
    this.selected = new Set([...this.flows.entries()].filter(([, f]) => f > 0).map(([k]) => k));
    this.note = '';
    this.message = '';
    audio.play('edgeDeselect');
    this.paint();
    this.updateStatus();
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    this.foldOrderAfterFirstMove();
    const point = this.renderer.pointerPosition(event);
    const edge = this.renderer.edgeAt(this.level, point, event.pointerType !== 'mouse');
    if (!edge) return;
    if (this.isFlowLevel) {
      this.traceFlow(edge.id);
      return;
    }

    const next = new Set(this.selected);
    if (next.has(edge.id)) {
      next.delete(edge.id);
      this.undoCount++;
      audio.play('edgeDeselect');
    } else {
      next.add(edge.id);
      if (!this.engine.validateMove(next)) {
        this.message = this.rejectionReason();
        audio.play('invalidMove');
        this.paint();
        this.updateStatus();
        return;
      }
      this.edgesSelectedDuringSession++;
      audio.play('edgeSelect');
    }

    this.selected = next;
    this.message = '';
    this.paint();
    this.updateStatus();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const point = this.renderer.pointerPosition(event);
    const edge = this.renderer.edgeAt(this.level, point);
    const id = edge?.id ?? null;
    if (id === this.hovered) return;

    this.hovered = id;
    this.canvas.style.cursor = id ? 'pointer' : 'default';
    this.paintNextFrame();
  };

  private readonly handlePointerLeave = (): void => {
    this.hovered = null;
    this.paintNextFrame();
  };

  private paintNextFrame(): void {
    if (this.hoverFrame) return;
    this.hoverFrame = requestAnimationFrame(() => {
      this.hoverFrame = 0;
      this.paint();
    });
  }

  private readonly handleClick = (event: Event): void => {
    const element = event.target as HTMLElement;
    const target = element.closest('[data-action]');
    if (target?.getAttribute('data-action') === 'briefing-next') {
      this.advanceBriefing();
      return;
    }
    if (element.closest('[data-role="order"]')) {
      this.orderRead = true;
      this.setOrderOpen(!this.order.classList.contains('is-open'));
      return;
    }
    if (!target) return;

    switch (target.getAttribute('data-action')) {
      case 'dismiss-rotate':
        target.closest('[data-role="rotate"]')?.remove();
        break;
      case 'exit':
        this.callbacks.onExit();
        break;
      case 'reset':
        audio.play('buttonTap');
        this.undoCount += this.selected.size;
        this.selected = new Set();
        for (const key of this.flows.keys()) this.flows.set(key, 0);
        this.route = startRoute(this.level.graph);
        this.flowHistory = [];
        this.message = '';
        this.note = '';
        this.paint();
        this.updateStatus();
        break;
      case 'undo':
        this.undoFlow();
        break;
      case 'hint':
        this.showNextHint();
        break;
      case 'submit':
        this.submit();
        break;
    }
  };

  private rejectionReason(): string {
    switch (this.level.type) {
      case 'shortestPath':
      case 'shortestPathPlanar':
        return 'That would branch or double back — a route has to stay a single path.';
      case 'mst':
        return 'That closes a loop. A spanning network never needs one.';
      case 'matching':
        return 'That node is already paired.';
      case 'interdiction':
        return `You can only remove ${this.level.budget} edge(s).`;
      case 'tsp':
        return 'That node already has two roads. A tour passes through once.';
      default:
        return 'That move is not allowed here.';
    }
  }

  private nodeName(nodeId: string): string {
    const node = this.level.graph.nodes.find((n) => n.id === nodeId);
    return node?.label || nodeId;
  }

  private showNextHint(): void {
    if (this.hintTier >= this.level.hints.length) return;
    this.hintTier++;
    audio.play('hintReveal');

    const hint = this.level.hints[this.hintTier - 1];
    const panel = this.root.querySelector<HTMLElement>('[data-role="hint"]')!;
    panel.hidden = false;
    panel.innerHTML = fieldNoteMarkup(hint?.text ?? '', this.hintTier, this.level.hints.length);
    this.paint();
  }

  private get hintEdges(): Set<string> {
    const hint = this.level.hints[this.hintTier - 1];
    return new Set(hint?.highlightEdges ?? []);
  }

  private currentScore(): number {
    return this.engine.calculateScore(this.selected, this.isFlowLevel ? this.flows : undefined);
  }

  private updateStatus(): void {
    const score = this.currentScore();
    const el = this.root.querySelector<HTMLElement>('[data-role="score"]')!;
    const display = score === Infinity ? '∞' : formatNumber(score);
    el.innerHTML = `<span class="puzzle__score-value">${display}</span><span class="puzzle__score-target">target ${formatNumber(this.level.optimalValue)}</span>`;

    const messageEl = this.root.querySelector<HTMLElement>('[data-role="message"]')!;
    messageEl.textContent = this.message || this.note;
    messageEl.classList.toggle('is-error', this.message !== '');
    messageEl.classList.toggle('is-note', this.message === '' && this.note !== '');
  }

  private submit(): void {
    const flows = this.isFlowLevel ? this.flows : undefined;
    if (!this.engine.isSolutionComplete(this.selected, flows)) {
      this.message = this.incompleteReason();
      audio.play('invalidMove');
      this.updateStatus();
      return;
    }

    const score = this.engine.calculateScore(this.selected, flows);
    const stars = calculateStars(score, this.level.thresholds, this.level.type);
    audio.play('levelComplete');
    this.callbacks.onSolved({
      level: this.level,
      score,
      stars,
      selectedEdgeIds: [...this.selected],
      ...(this.isFlowLevel ? { edgeFlows: Object.fromEntries(this.flows) } : {}),
      durationMs: Date.now() - this.startedAt,
      hintsUsed: this.hintTier,
      undoCount: this.undoCount,
      edgesSelectedDuringSession: this.edgesSelectedDuringSession,
    });
  }

  private incompleteReason(): string {
    switch (this.level.type) {
      case 'shortestPath':
      case 'shortestPathPlanar':
        return 'Your route does not connect the source to the sink yet.';
      case 'mst':
        return 'Not every node is connected into one network yet.';
      case 'maxFlow':
        return this.selected.size === 0
          ? 'Open at least one route from the source to the sink first.'
          : 'Flow has to be conserved: what enters a node must leave it again.';
      case 'minCut':
        return 'The source can still reach the sink. Cut more conduits.';
      case 'matching':
        return 'There are still pairs left to make.';
      case 'interdiction':
        return 'Select at least one edge to remove.';
      case 'tsp':
        return 'A tour visits every node exactly once and returns to the start.';
    }
  }

  private paint(): void {
    cancelAnimationFrame(this.hoverFrame);
    this.hoverFrame = 0;
    const state: CanvasState = {
      selectedEdgeIds: this.selected,
      hoveredEdgeId: this.hovered,
      hintEdgeIds: this.hintEdges,
      edgeFlows: this.flows,
      cutMode: this.isCutLevel,
      showFlows: this.isFlowLevel,
      routeEdgeIds: new Set(this.route.steps.map((step) => step.edgeId)),
      routeHeadId: this.route.steps.length > 0 ? this.route.head : null,
    };
    this.renderer.render(this.level, themeFor(this.themeId), state);
  }
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export { escapeHtml };

export function calendarMarkup(index: number, total: number): string {
  return calendarSheet('Gearfall', String(index), `of ${total}`, `Run ${index} of ${total}`);
}

export function calendarSheet(city: string, num: string, of: string, label: string): string {
  return `
    <div class="calendar" aria-label="${escapeHtml(label)}">
      <span class="calendar__city">${escapeHtml(city)}</span>
      <span class="calendar__num">${escapeHtml(num)}</span>
      <span class="calendar__of">${escapeHtml(of)}</span>
    </div>
  `;
}

const HINT_VOICES: ReadonlyArray<readonly [RegExp, string, string]> = [
  [/^Pip\b/, 'pip', 'Pip'],
  [/^Kit\b/, 'kit', 'Kit'],
  [/^Rekka\b/, 'rekka', 'Rekka'],
];

function fieldNoteMarkup(text: string, tier: number, total: number): string {
  const voice = HINT_VOICES.find(([pattern]) => pattern.test(text));
  const from = voice ? `From ${voice[2]} · ` : '';
  const photo = voice
    ? `<img class="note__photo" src="story/portraits/${voice[1]}.png" alt="" width="48" height="48" />`
    : '';
  return `
    <span class="note__staple" aria-hidden="true"></span>
    ${photo}
    <p class="note__kicker">${from}Field note ${tier}/${total}</p>
    <p class="note__body">${escapeHtml(text)}</p>
  `;
}
